import * as React from 'react';
import { X, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useMedia, type MediaAsset, type MediaKind } from '@/lib/media';
import { env } from '@/lib/env';
import { cn } from '@/lib/cn';

export interface MediaPickerProps {
  /** Restrict to one or more kinds. Default: any. */
  kinds?: MediaKind[];
  /** Require consent_signed_at? For team/patient photos this should be true. */
  requireConsent?: boolean;
  /** Pre-selected asset id. */
  selectedId?: string | null;
  /** Modal title. */
  title?: string;
  onSelect: (asset: MediaAsset) => void;
  onClear?: () => void;
  onClose: () => void;
}

/**
 * Modal that lists existing media assets, filtered by kind/consent, and
 * returns the chosen one. Use from any editor that needs to pick a media
 * reference (team photo, logo, gallery, etc).
 */
export function MediaPicker({
  kinds,
  requireConsent = false,
  selectedId,
  title = 'Wybierz plik z biblioteki',
  onSelect,
  onClear,
  onClose,
}: MediaPickerProps) {
  const media = useMedia();
  const filtered = (media.data?.assets ?? []).filter((a) => {
    if (kinds && !kinds.includes(a.kind)) return false;
    if (requireConsent && a.consent_signed_at == null) return false;
    return true;
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 grid place-items-center bg-text/40 p-4 overflow-y-auto"
    >
      <div className="relative w-full max-w-3xl rounded-[var(--radius-card)] bg-bg p-6 shadow-xl ring-1 ring-black/10">
        <button
          type="button"
          className="absolute right-4 top-4 rounded-md p-1 text-text-muted hover:text-text"
          onClick={onClose}
          aria-label="Zamknij"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
        <h2 className="font-display text-lg font-semibold text-text">{title}</h2>
        <p className="mt-1 text-xs text-text-muted">
          {requireConsent
            ? 'Pokazujemy tylko pliki z podpisaną zgodą.'
            : 'Pokazujemy aktywne pliki z biblioteki.'}
        </p>

        {media.isPending ? (
          <p className="mt-6 text-sm text-text-muted">Ładowanie biblioteki…</p>
        ) : filtered.length === 0 ? (
          <p className="mt-6 rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
            Brak plików. Dodaj zdjęcia w zakładce „Media" i wróć tutaj.
          </p>
        ) : (
          <ul role="list" className="mt-5 grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3 md:grid-cols-4">
            {filtered.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => onSelect(a)}
                  className={cn(
                    'group relative block w-full overflow-hidden rounded-md bg-surface ring-1 transition-all',
                    selectedId === a.id
                      ? 'ring-2 ring-accent'
                      : 'ring-black/5 hover:ring-accent/50'
                  )}
                >
                  <div className="aspect-square">
                    <img
                      src={`${env.apiBaseUrl}${a.public_url}`}
                      alt={a.alt_text ?? a.original_filename}
                      loading="lazy"
                      className="size-full object-cover"
                    />
                  </div>
                  {selectedId === a.id ? (
                    <span className="absolute right-2 top-2 inline-flex size-6 items-center justify-center rounded-full bg-accent text-text-inverted">
                      <Check className="size-3.5" aria-hidden="true" />
                    </span>
                  ) : null}
                  <p className="truncate p-2 text-left text-xs text-text-muted">
                    {a.alt_text || a.original_filename}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-5 flex justify-between gap-3 border-t border-border pt-4">
          {onClear ? (
            <Button type="button" variant="ghost" onClick={onClear}>
              Usuń powiązanie
            </Button>
          ) : (
            <span />
          )}
          <Button type="button" variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
        </div>
      </div>
    </div>
  );
}
